/**
 * Lesbare Zusammenfassung einer reinen XML-E-Rechnung (XRechnung/ZUGFeRD, CII oder UBL),
 * damit Freigebende den Beleg ohne PDF prüfen können. Liest nur, rechnet nichts nach.
 */
import { XMLParser } from 'fast-xml-parser';

export type EinvoiceXmlParty = {
  name: string | null;
  street: string | null;
  postcodeCity: string | null;
  country: string | null;
  vatId: string | null;
  /** Steuernummer (CII schemeID FC), falls keine USt-IdNr. angegeben ist. */
  taxNumber: string | null;
};

export type EinvoiceXmlLine = {
  id: string | null;
  name: string | null;
  quantity: number | null;
  unit: string | null;
  unitPrice: number | null;
  lineTotal: number | null;
  taxRate: number | null;
};

export type EinvoiceXmlSummary = {
  syntax: 'CII' | 'UBL';
  invoiceNumber: string | null;
  issueDate: string | null;
  dueDate: string | null;
  typeCode: string | null;
  currency: string | null;
  buyerReference: string | null;
  seller: EinvoiceXmlParty;
  buyer: EinvoiceXmlParty;
  lines: EinvoiceXmlLine[];
  taxes: Array<{ category: string | null; rate: number | null; basis: number | null; tax: number | null }>;
  totals: { net: number | null; tax: number | null; gross: number | null; due: number | null };
  iban: string | null;
  paymentTerms: string | null;
  notes: string[];
};

type Node = Record<string, unknown>;

function asArray<T>(value: T | T[] | null | undefined): T[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

function text(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string' || typeof value === 'number') {
    const s = String(value).trim();
    return s || null;
  }
  if (typeof value === 'object') {
    const inner = (value as Node)['#text'];
    if (inner != null) return text(inner);
  }
  return null;
}

function num(value: unknown): number | null {
  const s = text(value);
  if (s == null) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function get(node: unknown, ...path: string[]): unknown {
  let cur: unknown = node;
  for (const key of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = Array.isArray(cur) ? (cur[0] as Node)?.[key] : (cur as Node)[key];
  }
  return Array.isArray(cur) ? cur[0] : cur;
}

/** CII-Datum (Format 102: YYYYMMDD) bzw. UBL-Datum (YYYY-MM-DD) nach YYYY-MM-DD. */
function isoDate(value: unknown): string | null {
  const raw = text(get(value, 'DateTimeString')) ?? text(value);
  if (!raw) return null;
  if (/^\d{8}$/.test(raw)) return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  return /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : raw;
}

function ciiParty(node: unknown): EinvoiceXmlParty {
  const address = get(node, 'PostalTradeAddress');
  const regs = asArray(get(node, 'SpecifiedTaxRegistration') as Node | Node[] | undefined);
  const vat = regs
    .map((r) => get(r, 'ID'))
    .find((id) => (id as Node)?.['@_schemeID'] === 'VA' || /^[A-Z]{2}/.test(String(text(id) ?? '')));
  const taxNumber = regs.map((r) => get(r, 'ID')).find((id) => (id as Node)?.['@_schemeID'] === 'FC');
  const postcode = text(get(address, 'PostcodeCode'));
  const city = text(get(address, 'CityName'));
  return {
    name: text(get(node, 'Name')),
    street: text(get(address, 'LineOne')),
    postcodeCity: [postcode, city].filter(Boolean).join(' ') || null,
    country: text(get(address, 'CountryID')),
    vatId: text(vat),
    taxNumber: text(taxNumber),
  };
}

function parseCii(root: Node): EinvoiceXmlSummary {
  const doc = get(root, 'ExchangedDocument');
  const tx = get(root, 'SupplyChainTradeTransaction');
  const agreement = get(tx, 'ApplicableHeaderTradeAgreement');
  const settlement = get(tx, 'ApplicableHeaderTradeSettlement');
  const sums = get(settlement, 'SpecifiedTradeSettlementHeaderMonetarySummation');
  const lineNodes = asArray((tx as Node | undefined)?.['IncludedSupplyChainTradeLineItem'] as Node | Node[] | undefined);
  const taxSum = asArray((sums as Node | undefined)?.['TaxTotalAmount'] as unknown[] | unknown);
  return {
    syntax: 'CII',
    invoiceNumber: text(get(doc, 'ID')),
    issueDate: isoDate(get(doc, 'IssueDateTime')),
    dueDate: isoDate(get(settlement, 'SpecifiedTradePaymentTerms', 'DueDateDateTime')),
    typeCode: text(get(doc, 'TypeCode')),
    currency: text(get(settlement, 'InvoiceCurrencyCode')),
    buyerReference: text(get(agreement, 'BuyerReference')),
    seller: ciiParty(get(agreement, 'SellerTradeParty')),
    buyer: ciiParty(get(agreement, 'BuyerTradeParty')),
    lines: lineNodes.map((line) => ({
      id: text(get(line, 'AssociatedDocumentLineDocument', 'LineID')),
      name: text(get(line, 'SpecifiedTradeProduct', 'Name')),
      quantity: num(get(line, 'SpecifiedLineTradeDelivery', 'BilledQuantity')),
      unit: ((get(line, 'SpecifiedLineTradeDelivery', 'BilledQuantity') as Node | undefined)?.['@_unitCode'] as string) ?? null,
      unitPrice: num(get(line, 'SpecifiedLineTradeAgreement', 'NetPriceProductTradePrice', 'ChargeAmount')),
      lineTotal: num(get(line, 'SpecifiedLineTradeSettlement', 'SpecifiedTradeSettlementLineMonetarySummation', 'LineTotalAmount')),
      taxRate: num(get(line, 'SpecifiedLineTradeSettlement', 'ApplicableTradeTax', 'RateApplicablePercent')),
    })),
    taxes: asArray((settlement as Node | undefined)?.['ApplicableTradeTax'] as Node | Node[] | undefined).map((t) => ({
      category: text(get(t, 'CategoryCode')),
      rate: num(get(t, 'RateApplicablePercent')),
      basis: num(get(t, 'BasisAmount')),
      tax: num(get(t, 'CalculatedAmount')),
    })),
    totals: {
      net: num(get(sums, 'TaxBasisTotalAmount')),
      tax: num(taxSum[0]),
      gross: num(get(sums, 'GrandTotalAmount')),
      due: num(get(sums, 'DuePayableAmount')),
    },
    iban: text(get(settlement, 'SpecifiedTradeSettlementPaymentMeans', 'PayeePartyCreditorFinancialAccount', 'IBANID')),
    paymentTerms: text(get(settlement, 'SpecifiedTradePaymentTerms', 'Description')),
    notes: asArray((doc as Node | undefined)?.['IncludedNote'] as Node | Node[] | undefined)
      .map((n) => text(get(n, 'Content')))
      .filter((n): n is string => Boolean(n)),
  };
}

function ublParty(node: unknown): EinvoiceXmlParty {
  const party = get(node, 'Party');
  const address = get(party, 'PostalAddress');
  const postcode = text(get(address, 'PostalZone'));
  const city = text(get(address, 'CityName'));
  return {
    name: text(get(party, 'PartyLegalEntity', 'RegistrationName')) ?? text(get(party, 'PartyName', 'Name')),
    street: text(get(address, 'StreetName')),
    postcodeCity: [postcode, city].filter(Boolean).join(' ') || null,
    country: text(get(address, 'Country', 'IdentificationCode')),
    vatId: text(get(party, 'PartyTaxScheme', 'CompanyID')),
    taxNumber: null,
  };
}

function parseUbl(root: Node, isCredit: boolean): EinvoiceXmlSummary {
  const lineKey = isCredit ? 'CreditNoteLine' : 'InvoiceLine';
  const qtyKey = isCredit ? 'CreditedQuantity' : 'InvoicedQuantity';
  const totals = get(root, 'LegalMonetaryTotal');
  const taxTotal = get(root, 'TaxTotal');
  return {
    syntax: 'UBL',
    invoiceNumber: text(get(root, 'ID')),
    issueDate: isoDate(get(root, 'IssueDate')),
    dueDate: isoDate(get(root, 'DueDate')),
    typeCode: text(get(root, isCredit ? 'CreditNoteTypeCode' : 'InvoiceTypeCode')),
    currency: text(get(root, 'DocumentCurrencyCode')),
    buyerReference: text(get(root, 'BuyerReference')),
    seller: ublParty(get(root, 'AccountingSupplierParty')),
    buyer: ublParty(get(root, 'AccountingCustomerParty')),
    lines: asArray(root[lineKey] as Node | Node[] | undefined).map((line) => ({
      id: text(get(line, 'ID')),
      name: text(get(line, 'Item', 'Name')),
      quantity: num(get(line, qtyKey)),
      unit: ((get(line, qtyKey) as Node | undefined)?.['@_unitCode'] as string) ?? null,
      unitPrice: num(get(line, 'Price', 'PriceAmount')),
      lineTotal: num(get(line, 'LineExtensionAmount')),
      taxRate: num(get(line, 'Item', 'ClassifiedTaxCategory', 'Percent')),
    })),
    taxes: asArray((taxTotal as Node | undefined)?.['TaxSubtotal'] as Node | Node[] | undefined).map((t) => ({
      category: text(get(t, 'TaxCategory', 'ID')),
      rate: num(get(t, 'TaxCategory', 'Percent')),
      basis: num(get(t, 'TaxableAmount')),
      tax: num(get(t, 'TaxAmount')),
    })),
    totals: {
      net: num(get(totals, 'TaxExclusiveAmount')),
      tax: num(get(taxTotal, 'TaxAmount')),
      gross: num(get(totals, 'TaxInclusiveAmount')),
      due: num(get(totals, 'PayableAmount')),
    },
    iban: text(get(root, 'PaymentMeans', 'PayeeFinancialAccount', 'ID')),
    paymentTerms: text(get(root, 'PaymentTerms', 'Note')),
    notes: asArray(root['Note'] as unknown[] | unknown).map((n) => text(n)).filter((n): n is string => Boolean(n)),
  };
}

/** null, wenn das XML keine bekannte E-Rechnung ist. */
export function summarizeEinvoiceXml(xml: string): EinvoiceXmlSummary | null {
  let parsed: Node;
  try {
    parsed = new XMLParser({
      ignoreAttributes: false,
      removeNSPrefix: true,
      parseTagValue: false,
      trimValues: true,
    }).parse(xml) as Node;
  } catch {
    return null;
  }
  if (parsed.CrossIndustryInvoice) return parseCii(parsed.CrossIndustryInvoice as Node);
  if (parsed.Invoice) return parseUbl(parsed.Invoice as Node, false);
  if (parsed.CreditNote) return parseUbl(parsed.CreditNote as Node, true);
  return null;
}
